import { keyframes } from '@emotion/react';
import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ContentLogKeys, EngagementKeys, IssueKeys, ServiceKeys } from '../../shared/constants/IssueAndServiceKeys';
import { TrackerDataType, transitionTime } from '../../shared/constants/constants';
import { TableCell, TableRow, IconButton, Checkbox, Box } from '@mui/material';
import TrackerTableDataCell from './TrackerTableDataCell';
import TrackerTableEditorCell from './TrackerTableEditorCell';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import TrackerCollapsedRow from './TrackerCollapsedRow';
import { useDispatch } from 'react-redux';
import { openSnackbar } from '../AppSnackbar/snackbarSlice';
import findOrganizationFromContact from '../../shared/functions/findOrganizationFromContact';
import QueryAdderDrawer from '../QueryAdder/QueryAdderDrawer';
import QueryAdder from '../QueryAdder/QueryAdder';
import { processCellsForAutoComplete } from '../../shared/functions/autoCompleteProcessing';

const loadIssue = keyframes({
  '0%': { backgroundColor: '#a9d5a9' },
  '100%': { backgroundColor: 'white' },
});

const rowEditorTitleMapping = {
  [TrackerDataType.SERVICE]: 'Service',
  [TrackerDataType.MEDIA_INTERACTION]: 'Interaction',
  [TrackerDataType.ENGAGEMENT]: 'Engagement',
  [TrackerDataType.CONTENT_LOG]: 'Content Log',
};

export default function TrackerTableDataRow(props) {
  const {
    row,
    visibleCols,
    dropdownTable,
    isStripedRow,
    eventHandler,
    linkedEventHandler,
    secondaryLinkedEventHandler,
    tertiaryLinkedEventHandler,
    isBulkEdit,
    isBulkEditSelectedRow,
    onBulkEditSelectRow,
  } = props;
  const dispatch = useDispatch();
  const [cells, setCells] = useState(row.cells);
  const [isEditMode, setIsEditMode] = useState(false);
  const [detailedView, setDetailedView] = useState(false);
  //const [isDragActive, setDragActive] = useState(false);

  useEffect(() => {
    if (!isEditMode) {
      setCells(row.cells);
    }
  }, [row, isEditMode]);

  const { isNewlyAdded } = eventHandler.useListenOnCreation(row.id);

  const onEdit = (val, index) => {
    let newVal = eventHandler.editItem(val, index);
    setCells((cells) => ({ ...cells, [index]: newVal }));

    index === IssueKeys.outlet && setCells((cells) => ({ ...cells, Journalist: null }));
    index === IssueKeys.journalist &&
      setCells((cells) => ({ ...cells, Outlet: findOrganizationFromContact(val, dropdownTable['outlet'], 'Org') }));

    index === EngagementKeys.organization && setCells((cells) => ({ ...cells, CommunityMember: null }));
    index === EngagementKeys.communityMember &&
      setCells((cells) => ({
        ...cells,
        Organization: findOrganizationFromContact(val, dropdownTable['organization'], 'Organization'),
      }));
  };

  const handleEditMode = (editMode) => {
    setIsEditMode(editMode);
    setDetailedView(editMode);
  };

  const onDelete = async () => {
    const deleted = await eventHandler.deleteItem(row.id);
    if (deleted?.data) {
      switch (eventHandler.type) {
        case TrackerDataType.MEDIA_INTERACTION:
          dispatch(
            openSnackbar({
              severity: 'warning',
              message: 'issue deleted!',
              callbackData: deleted.data,
              eventType: 'deleteIssue',
            })
          );
          break;
        case TrackerDataType.SERVICE:
          dispatch(
            openSnackbar({
              severity: 'warning',
              message: 'service deleted!',
              callbackData: deleted.data,
              eventType: 'deleteService',
            })
          );
          break;
        case TrackerDataType.ENGAGEMENT:
          dispatch(
            openSnackbar({
              severity: 'warning',
              message: 'engagement deleted!',
              callbackData: deleted.data,
              eventType: 'deleteEngagement',
            })
          );
          break;
        case TrackerDataType.CONTENT_LOG:
          dispatch(
            openSnackbar({
              severity: 'warning',
              message: 'content log deleted!',
            })
          );
          break;
        default:
          console.error('unknown eventHandler type');
          break;
      }
    }
  };

  const onDuplicate = async () => {
    // should duplicate in backend
    let trackerTypeStr;
    switch (eventHandler.type) {
      case TrackerDataType.SERVICE:
        trackerTypeStr = 'Service';
        break;
      case TrackerDataType.MEDIA_INTERACTION:
        trackerTypeStr = 'Media Interaction';
        break;
      case TrackerDataType.ENGAGEMENT:
        trackerTypeStr = 'Engagement';
        break;
      case TrackerDataType.CONTENT_LOG:
        trackerTypeStr = 'Content Log';
        break;
      default:
        trackerTypeStr = 'Service';
        break;
    }

    try {
      const duplicateId = row.id;
      await eventHandler.duplicateItem(duplicateId);
      dispatch(
        openSnackbar({
          severity: 'success',
          message: trackerTypeStr + ' Duplicated!',
        })
      );
    } catch (error) {
      console.error('duplicate failed', error);
      dispatch(
        openSnackbar({
          severity: 'error',
          message: 'Failed to duplicate ' + trackerTypeStr,
        })
      );
    }
  };

  // const handleDrag = (e) => {
  //   e.preventDefault();
  //   e.stopPropagation();
  //   if (detailedView) {
  //     console.log(e.type);

  //     if (e.type === 'dragenter' || e.type === 'dragover') {
  //       setDragActive(true);
  //     } else if (e.type === 'dragleave') {
  //       setDragActive(false);
  //     }
  //   }
  // };

  // const handleDrop = (e) => {
  //   e.preventDefault();
  //   e.stopPropagation();
  //   setDragActive(false);
  //   if (e.dataTransfer.files && e.dataTransfer.files[0] && isDragActive) {
  //     const headers = { 'content-type': 'multipart/form-data' };
  //     let files = new FormData();

  //     //TODO handle zero or multiple files
  //     let f = e.dataTransfer.files[0];
  //     files.append('file', f);

  //     API.put(`/issue/${row.id}/upload`, files, { headers: headers })
  //       .then((response) => {
  //         if (response.statusText !== 'OK') {
  //           alert(response.statusText);
  //         }

  //         //TODO add file to interaction
  //         return response;
  //       })
  //       .catch((e) => {
  //         alert('post failed - ', e);
  //       });

  //     e.dataTransfer.clearData();
  //   }
  // };

  const processedCells = processCellsForAutoComplete(cells, dropdownTable, row.id);
  const getInitialFiles = (cells) => {
    switch (eventHandler.type) {
      case TrackerDataType.SERVICE:
        return cells[ServiceKeys.file];
      case TrackerDataType.MEDIA_INTERACTION:
        return cells[IssueKeys.file];
      case TrackerDataType.ENGAGEMENT:
        return cells[EngagementKeys.file];
      case TrackerDataType.CONTENT_LOG:
        return cells[ContentLogKeys.file];
      default:
        return [];
    }
  };

  let tableDataRowSecondCol = (
    <TrackerTableEditorCell
      handleEditMode={handleEditMode}
      onDuplicate={onDuplicate}
      sx={{
        padding: '6px',
        textAlign: 'center',
        borderRight: 'none',
        borderLeft: 'none',
        '& > button': {
          padding: '4px',
        },
      }}
      showDuplicate={
        eventHandler.type === TrackerDataType.MEDIA_INTERACTION ||
        eventHandler.type === TrackerDataType.SERVICE ||
        eventHandler.type === TrackerDataType.ENGAGEMENT ||
        eventHandler.type === TrackerDataType.CONTENT_LOG
      }
      type={eventHandler.type}
      rowId={row.id}
    />
  );
  if (isBulkEdit) {
    tableDataRowSecondCol = (
      <TableCell
        sx={{
          padding: '6px',
          textAlign: 'center',
          borderRight: 'none',
          borderLeft: 'none',
          '& > button': {
            padding: '4px',
          },
        }}
      >
        <Box sx={{ display: 'flex', width: '48px', height: '48px', alignItems: 'center', justifyContent: 'start' }}>
          <Checkbox checked={isBulkEditSelectedRow} onChange={(e) => onBulkEditSelectRow(e.target.checked, row.id)} />
        </Box>
      </TableCell>
    );
  }

  return (
    <>
      <TableRow
        // onDragEnter={handleDrag}
        // onDragLeave={handleDrag}
        // onDragOver={handleDrag}
        // onDrop={handleDrop}
        sx={[
          isStripedRow ? { backgroundColor: '#f0f2f5' } : null,
          isNewlyAdded ? { animation: `${loadIssue} ${transitionTime}ms` } : null,
        ]}
        key={row.id}
      >
        <TableCell
          sx={{
            padding: '6px',
            textAlign: 'center',
            borderRight: 'none',
            borderLeft: 'none',
            '& > button': {
              padding: '4px',
            },
          }}
        >
          <IconButton aria-label="expand row" size="small" onClick={() => setDetailedView(!detailedView)}>
            {detailedView ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        {tableDataRowSecondCol}
        {visibleCols.map((key) => (
          <TrackerTableDataCell
            type={eventHandler.type}
            key={key}
            cells={cells}
            index={key}
            isEditMode={false}
            onEdit={onEdit}
            dropdownTable={dropdownTable}
            cellSx={{ padding: '6px', textAlign: 'center' }}
          />
        ))}
      </TableRow>
      {detailedView && (
        <TrackerCollapsedRow
          open={detailedView}
          dropdownTable={dropdownTable}
          isEditMode={false}
          onEdit={onEdit}
          cells={cells}
          colSpan={visibleCols.length + 2}
          type={eventHandler.type}
          linkedEventHandler={linkedEventHandler}
          secondaryLinkedEventHandler={secondaryLinkedEventHandler}
          tertiaryLinkedEventHandler={tertiaryLinkedEventHandler}
          id={row.id}
        />
      )}
      <QueryAdderDrawer openForm={isEditMode} setOpenForm={setIsEditMode}>
        <QueryAdder
          title={`Editing ${rowEditorTitleMapping[eventHandler.type]}`}
          dropdownTable={dropdownTable}
          setOpenForm={setIsEditMode}
          eventHandler={eventHandler}
          cells={processedCells}
          issueId={row.id}
          linkedType={eventHandler.type}
          initialFiles={getInitialFiles(processedCells)}
          isUpdatingRow={true}
          onDelete={onDelete}
        />
      </QueryAdderDrawer>
    </>
  );
}

TrackerTableDataRow.propTypes = {
  row: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    cells: PropTypes.object.isRequired,
  }).isRequired,
  visibleCols: PropTypes.arrayOf(PropTypes.string).isRequired,
  dropdownTable: PropTypes.object.isRequired,
  isStripedRow: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
  eventHandler: PropTypes.shape({
    type: PropTypes.oneOf([
      TrackerDataType.MEDIA_INTERACTION,
      TrackerDataType.SERVICE,
      TrackerDataType.ENGAGEMENT,
      TrackerDataType.CONTENT_LOG,
    ]).isRequired,
    useListenOnCreation: PropTypes.func.isRequired,
    editItem: PropTypes.func.isRequired,
    deleteItem: PropTypes.func.isRequired,
    duplicateItem: PropTypes.func.isRequired,
  }).isRequired,
  linkedEventHandler: PropTypes.shape({
    type: PropTypes.oneOf([
      TrackerDataType.MEDIA_INTERACTION,
      TrackerDataType.SERVICE,
      TrackerDataType.ENGAGEMENT,
      TrackerDataType.CONTENT_LOG,
    ]),
    newItemModalTitle: PropTypes.string,
  }),
  secondaryLinkedEventHandler: PropTypes.shape({
    type: PropTypes.oneOf([
      TrackerDataType.MEDIA_INTERACTION,
      TrackerDataType.SERVICE,
      TrackerDataType.ENGAGEMENT,
      TrackerDataType.CONTENT_LOG,
    ]),
    newItemModalTitle: PropTypes.string,
  }),
  tertiaryLinkedEventHandler: PropTypes.shape({
    type: PropTypes.oneOf([
      TrackerDataType.MEDIA_INTERACTION,
      TrackerDataType.SERVICE,
      TrackerDataType.ENGAGEMENT,
      TrackerDataType.CONTENT_LOG,
    ]),
    newItemModalTitle: PropTypes.string,
  }),
  isBulkEdit: PropTypes.bool,
  isBulkEditSelectedRow: PropTypes.bool,
  onBulkEditSelectRow: PropTypes.func,
};
